import { Colors } from "@blueprintjs/core";
import DOMPurify from "dompurify";
import emojiRegexText from "emoji-regex/es2015/text";
import marked from "marked";
import { Configuration } from "../common";

const IMAGE_URL_REGEX = /^(http(s?):)([/|.|\w|\s|-])*\.(?:jpg|gif|png)$/g;
let dompurify: DOMPurify.DOMPurifyI | undefined;

if (typeof window !== "undefined") {
    dompurify = DOMPurify(window);
    dompurify.addHook("afterSanitizeAttributes", (node) => {
        // we can't do this with marked.Renderer because dompurify will strip out "target"
        if (node.tagName.toLowerCase() === "a") {
            node.setAttribute("target", "_blank");
            // https://developers.google.com/web/tools/lighthouse/audits/noopener#recommendations
            node.setAttribute("rel", "noopener noreferrer");
        }
    });
}

/**
 * Parse message text as Markdown, render it to sanitized HTML.
 */
export function renderMessage(message: string) {
    if (typeof dompurify === "undefined") {
        // TODO: consider using isomorphic-dompurify to avoid this guard
        return "Rendering chat messages with SSR is unsupported";
    }

    if (IMAGE_URL_REGEX.test(message)) {
        // shortcut: if the message is just a URL to an image, use markdown image syntax
        // the string will already have been trimmed in the chat input upon submission
        message = `![image](${message})`;
    }

    const html = marked(message);
    return dompurify.sanitize(html);
}

export function isOnlyEmojis(message: string): boolean {
    if (!message) {
        return false;
    }

    // remove emojis and whitespaces, see what's left
    return message.replace(emojiRegexText(), "").replace(/[\s\n]/gm, "") === "";
}

// for choreo performance views
export function getMessageColor(message: string) {
    if (isOnlyEmojis(message)) {
        return "pink";
    }

    for (const color of Object.keys(Configuration.PERFORMANCE_CHAT_KEYWORDS)) {
        if (color === "pink") {
            continue;
        }

        const keywords = (Configuration.PERFORMANCE_CHAT_KEYWORDS as any)[color];
        const messageWords = message.toLowerCase().split(" ");
        for (const keyword of keywords) {
            if (keyword.indexOf(" ") > 0) {
                if (message.toLowerCase().indexOf(keyword) >= 0) {
                    return color;
                }
            } else if (messageWords.indexOf(keyword) >= 0) {
                return color;
            }
        }
    }
    return undefined;
}

const colors = [
    Colors.COBALT3,
    Colors.FOREST2,
    Colors.VERMILION2,
    Colors.ROSE3,
    Colors.GREEN2,
    Colors.ORANGE1,
    Colors.INDIGO1,
    Colors.SEPIA2,
];

const userColors: any = {};
export function getUserMessageColor(userId: string, message: string): string {
    if (userColors[userId] == null) {
        const color = colors[Math.floor(Math.random() * colors.length)];
        userColors[userId] = color;
    }

    return userColors[userId];
}
